// Shared quantitative math for the Securities Research suite.
// Pure functions only — no domain types, no I/O, no React.

// ─── Basic statistics ────────────────────────────────────────

export function mean(xs: number[]): number {
  if (xs.length === 0) return 0
  let s = 0
  for (const x of xs) s += x
  return s / xs.length
}

/** Sample standard deviation (n − 1). */
export function stdev(xs: number[]): number {
  if (xs.length < 2) return 0
  const m = mean(xs)
  let s = 0
  for (const x of xs) s += (x - m) * (x - m)
  return Math.sqrt(s / (xs.length - 1))
}

export function correlation(xs: number[], ys: number[]): number {
  const n = Math.min(xs.length, ys.length)
  if (n < 3) return 0
  const mx = mean(xs.slice(0, n))
  const my = mean(ys.slice(0, n))
  let sxy = 0
  let sxx = 0
  let syy = 0
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - mx
    const dy = ys[i] - my
    sxy += dx * dy
    sxx += dx * dx
    syy += dy * dy
  }
  const denom = Math.sqrt(sxx * syy)
  return denom === 0 ? 0 : sxy / denom
}

/** Interpolated percentile of an ASCENDING-sorted array; p in [0, 1]. */
export function percentileSorted(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0
  const idx = p * (sorted.length - 1)
  const lo = Math.floor(idx)
  const hi = Math.ceil(idx)
  if (lo === hi) return sorted[lo]
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo)
}

export function standardize(xs: number[]): number[] {
  const m = mean(xs)
  const s = stdev(xs) || 1
  return xs.map((x) => (x - m) / s)
}

// ─── Seeded randomness ───────────────────────────────────────

/** FNV-1a hash of a string → 32-bit unsigned seed. */
export function hashSeed(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

/** Deterministic PRNG (mulberry32). Returns uniform [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** One standard normal draw via Box–Muller. */
export function gaussian(rand: () => number): number {
  let u = 0
  while (u === 0) u = rand()
  const v = rand()
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}

// ─── Normal distribution ─────────────────────────────────────

export function normalPdf(z: number): number {
  return Math.exp(-0.5 * z * z) / Math.sqrt(2 * Math.PI)
}

/** Abramowitz–Stegun approximation, |error| < 1.5e-7. */
export function normalCdf(z: number): number {
  const sign = z < 0 ? -1 : 1
  const x = Math.abs(z) / Math.SQRT2
  const t = 1 / (1 + 0.3275911 * x)
  const y =
    1 -
    (((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t) *
      Math.exp(-x * x)
  return 0.5 * (1 + sign * y)
}

// ─── Black–Scholes ───────────────────────────────────────────

export type OptionType = 'call' | 'put'

/**
 * Black–Scholes European option price.
 * s: spot, k: strike, t: years, sigma: vol (decimal), r/q: cont. rates (decimal).
 */
export function bsPrice(type: OptionType, s: number, k: number, t: number, sigma: number, r: number, q = 0): number {
  if (t <= 0 || sigma <= 0) {
    return type === 'call' ? Math.max(0, s - k) : Math.max(0, k - s)
  }
  const sqt = Math.sqrt(t)
  const d1 = (Math.log(s / k) + (r - q + 0.5 * sigma * sigma) * t) / (sigma * sqt)
  const d2 = d1 - sigma * sqt
  if (type === 'call') {
    return s * Math.exp(-q * t) * normalCdf(d1) - k * Math.exp(-r * t) * normalCdf(d2)
  }
  return k * Math.exp(-r * t) * normalCdf(-d2) - s * Math.exp(-q * t) * normalCdf(-d1)
}

export function bsDelta(type: OptionType, s: number, k: number, t: number, sigma: number, r: number, q = 0): number {
  if (t <= 0 || sigma <= 0) {
    if (type === 'call') return s > k ? 1 : 0
    return s < k ? -1 : 0
  }
  const d1 = (Math.log(s / k) + (r - q + 0.5 * sigma * sigma) * t) / (sigma * Math.sqrt(t))
  return type === 'call' ? Math.exp(-q * t) * normalCdf(d1) : Math.exp(-q * t) * (normalCdf(d1) - 1)
}

/**
 * Implied vol by bisection. Returns null when the price sits outside
 * the no-arbitrage range and no vol can reproduce it.
 */
export function impliedVol(
  type: OptionType,
  price: number,
  s: number,
  k: number,
  t: number,
  r: number,
  q = 0
): number | null {
  let lo = 0.005
  let hi = 3.0
  const pLo = bsPrice(type, s, k, t, lo, r, q)
  const pHi = bsPrice(type, s, k, t, hi, r, q)
  if (price <= pLo || price >= pHi) return null
  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2
    const p = bsPrice(type, s, k, t, mid, r, q)
    if (Math.abs(p - price) < 1e-7) return mid
    if (p < price) lo = mid
    else hi = mid
  }
  return (lo + hi) / 2
}

// ─── Linear algebra: OLS + eigen/PCA ─────────────────────────

/** Solve Ax = b by Gauss–Jordan with partial pivoting. Returns null if singular. */
export function solveLinear(a: number[][], b: number[]): number[] | null {
  const n = a.length
  const m = a.map((row, i) => [...row, b[i]])
  for (let col = 0; col < n; col++) {
    let pivot = col
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(m[row][col]) > Math.abs(m[pivot][col])) pivot = row
    }
    if (Math.abs(m[pivot][col]) < 1e-12) return null
    ;[m[col], m[pivot]] = [m[pivot], m[col]]
    for (let row = 0; row < n; row++) {
      if (row === col) continue
      const f = m[row][col] / m[col][col]
      for (let c = col; c <= n; c++) m[row][c] -= f * m[col][c]
    }
  }
  return m.map((row, i) => row[n] / m[i][i])
}

export interface OlsResult {
  intercept: number
  coefficients: number[]
  r2: number
}

/** Multiple OLS regression of y on predictor columns xs (each same length as y). */
export function olsRegression(y: number[], xs: number[][]): OlsResult {
  const n = y.length
  const k = xs.length
  // Design matrix columns: [1, x1..xk]. Build X'X and X'y.
  const dim = k + 1
  const xtx: number[][] = Array.from({ length: dim }, () => new Array(dim).fill(0))
  const xty: number[] = new Array(dim).fill(0)
  for (let i = 0; i < n; i++) {
    const row = [1, ...xs.map((col) => col[i])]
    for (let a = 0; a < dim; a++) {
      xty[a] += row[a] * y[i]
      for (let b = 0; b < dim; b++) xtx[a][b] += row[a] * row[b]
    }
  }
  const beta = solveLinear(xtx, xty) ?? new Array(dim).fill(0)
  const my = mean(y)
  let ssRes = 0
  let ssTot = 0
  for (let i = 0; i < n; i++) {
    let fit = beta[0]
    for (let j = 0; j < k; j++) fit += beta[j + 1] * xs[j][i]
    ssRes += (y[i] - fit) * (y[i] - fit)
    ssTot += (y[i] - my) * (y[i] - my)
  }
  return {
    intercept: beta[0],
    coefficients: beta.slice(1),
    r2: ssTot === 0 ? 0 : Math.max(0, 1 - ssRes / ssTot),
  }
}

/** Correlation matrix of data columns (each column one variable's series). */
export function correlationMatrix(cols: number[][]): number[][] {
  const k = cols.length
  const m: number[][] = Array.from({ length: k }, () => new Array(k).fill(0))
  for (let i = 0; i < k; i++) {
    m[i][i] = 1
    for (let j = i + 1; j < k; j++) {
      const c = correlation(cols[i], cols[j])
      m[i][j] = c
      m[j][i] = c
    }
  }
  return m
}

export interface EigenResult {
  /** Descending eigenvalues */
  values: number[]
  /** vectors[i] is the eigenvector for values[i] */
  vectors: number[][]
}

/** Jacobi eigenvalue algorithm for a real symmetric matrix. */
export function jacobiEigen(input: number[][]): EigenResult {
  const n = input.length
  const a = input.map((row) => [...row])
  // v starts as identity; columns accumulate the eigenvectors.
  const v: number[][] = Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) => (i === j ? 1 : 0))
  )
  for (let sweep = 0; sweep < 100; sweep++) {
    let off = 0
    for (let p = 0; p < n; p++) for (let q = p + 1; q < n; q++) off += a[p][q] * a[p][q]
    if (off < 1e-18) break
    for (let p = 0; p < n; p++) {
      for (let q = p + 1; q < n; q++) {
        if (Math.abs(a[p][q]) < 1e-15) continue
        const theta = (a[q][q] - a[p][p]) / (2 * a[p][q])
        const t = Math.sign(theta || 1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1))
        const c = 1 / Math.sqrt(t * t + 1)
        const s = t * c
        for (let i = 0; i < n; i++) {
          const aip = a[i][p]
          const aiq = a[i][q]
          a[i][p] = c * aip - s * aiq
          a[i][q] = s * aip + c * aiq
        }
        for (let i = 0; i < n; i++) {
          const api = a[p][i]
          const aqi = a[q][i]
          a[p][i] = c * api - s * aqi
          a[q][i] = s * api + c * aqi
        }
        for (let i = 0; i < n; i++) {
          const vip = v[i][p]
          const viq = v[i][q]
          v[i][p] = c * vip - s * viq
          v[i][q] = s * vip + c * viq
        }
      }
    }
  }
  const pairs = Array.from({ length: n }, (_, i) => ({
    value: a[i][i],
    vector: v.map((row) => row[i]),
  })).sort((x, y) => y.value - x.value)
  return { values: pairs.map((p) => p.value), vectors: pairs.map((p) => p.vector) }
}

export interface PcaResult {
  /** Descending eigenvalues of the correlation matrix */
  eigenvalues: number[]
  /** loadings[i][j] = loading of variable j on component i */
  loadings: number[][]
  /** Fraction of total variance per component, descending */
  varianceExplained: number[]
}

/** PCA on the correlation matrix of the given variable columns. */
export function pca(cols: number[][]): PcaResult {
  const corr = correlationMatrix(cols)
  const { values, vectors } = jacobiEigen(corr)
  const total = values.reduce((s, v) => s + Math.max(0, v), 0) || 1
  return {
    eigenvalues: values,
    loadings: vectors,
    varianceExplained: values.map((v) => Math.max(0, v) / total),
  }
}

// ─── Monte Carlo helpers ─────────────────────────────────────

export interface HistBin {
  start: number
  end: number
  count: number
}

export function histogram(values: number[], binCount: number): HistBin[] {
  if (values.length === 0) return []
  let lo = Infinity
  let hi = -Infinity
  for (const v of values) {
    if (v < lo) lo = v
    if (v > hi) hi = v
  }
  if (lo === hi) {
    hi = lo + 1
    lo = lo - 1
  }
  const width = (hi - lo) / binCount
  const bins: HistBin[] = Array.from({ length: binCount }, (_, i) => ({
    start: lo + i * width,
    end: lo + (i + 1) * width,
    count: 0,
  }))
  for (const v of values) {
    const idx = Math.min(binCount - 1, Math.floor((v - lo) / width))
    bins[idx].count++
  }
  return bins
}

export interface DistributionStats {
  mean: number
  median: number
  stdev: number
  p5: number
  p95: number
  probPositive: number
  var95: number
  cvar95: number
  best: number
  worst: number
}

export function distributionStats(values: number[]): DistributionStats {
  const sorted = [...values].sort((a, b) => a - b)
  const m = mean(sorted)
  const sd = stdev(sorted)
  const p5 = percentileSorted(sorted, 0.05)
  const tail = sorted.filter((v) => v <= p5)
  return {
    mean: m,
    median: percentileSorted(sorted, 0.5),
    stdev: sd,
    p5,
    p95: percentileSorted(sorted, 0.95),
    probPositive: sorted.filter((v) => v > 0).length / sorted.length,
    var95: Math.max(0, -p5),
    cvar95: Math.max(0, -mean(tail.length ? tail : [p5])),
    best: sorted[sorted.length - 1],
    worst: sorted[0],
  }
}
